import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'
import { mapActions } from 'vuex'
import VersionHistory from '@/components/VersionHistory/VersionHistory.vue'
import ArticleComments from '@/components/ArticleComments/ArticleComments.vue'
import { describeRange, resolveAnchor, wrapRange, clearMarks } from '@/utils/textAnchor'

export const COMMENT_MARK_CLASS = 'cg-comment-mark'

export default {
  name: 'MarkdownEditor',
  components: {
    VersionHistory,
    ArticleComments
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Write here...'
    },
    minHeight: {
      type: String,
      default: 'fit-content'
    },
    maxHeight: {
      type: String,
      default: 'auto'
    },
    showSaveStatus: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    saveError: {
      type: Boolean,
      default: false
    },
    defaultMode: {
      type: String,
      default: 'wysiwyg'
    },
    articleId: {
      type: [Number, String],
      default: null
    },
    contentplanId: {
      type: [Number, String],
      default: null
    },
    sharingKey: {
      type: String,
      default: null
    },
    showVersionHistory: {
      type: Boolean,
      default: false
    },
    showComments: {
      type: Boolean,
      default: false
    },
    lockMode: {
      type: Boolean,
      default: false
    },
    productShortcodeLookup: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      editor: null,
      currentMode: this.defaultMode,
      currentMarkdown: this.value || '',
      isInternalUpdate: false,
      lastEmittedValue: '',
      initialContent: '',
      versionHistoryOpen: false,
      shortcodeReferencesOpen: false,
      commentsOpen: false,
      reviewMode: false,
      threads: [],
      resolvedThreadIds: [],
      activeThreadId: null,
      pendingAnchor: null,
      isLoadingComments: false,
      commentPopover: null,
      selectionHandler: null,
      isPreviewingVersion: false,
      originalContentBeforePreview: '',
      isLoadingVersion: false,
      isViewerInstance: false,
      undoHandler: null,
      linkClickHandler: null,
      shortcodeLookup: {},
      shortcodeLookupTimeout: null,
      shortcodeLookupRequestKey: '',
      shortcodeLookupLoadedKey: ''
    }
  },
  watch: {
    value (newValue) {
      const nextValue = newValue || ''
      this.currentMarkdown = nextValue

      if (this.isPreviewingVersion) {
        this.originalContentBeforePreview = nextValue
        return
      }

      // Only update the editor if the editor's current value is different
      // This prevents infinite loops when the editor itself emits changes
      if (this.editor && this.currentMarkdownValue() !== nextValue && !this.isInternalUpdate) {
        this.isInternalUpdate = true
        this.setMarkdown(nextValue)
        this.lastEmittedValue = nextValue
        // Update initial content when value changes from outside
        this.initialContent = nextValue
        this.$nextTick(() => {
          this.isInternalUpdate = false
        })
      }

      this.scheduleShortcodeLookupFetch(nextValue)
    },
    contentplanId () {
      this.scheduleShortcodeLookupFetch()
    },
    articleId () {
      this.scheduleShortcodeLookupFetch()
    },
    sharingKey () {
      this.scheduleShortcodeLookupFetch()
    },
    hasShortcodeReferences (hasReferences) {
      if (!hasReferences && this.shortcodeReferencesOpen) this.shortcodeReferencesOpen = false
    },
    threads () {
      this.$nextTick(() => this.renderHighlights())
    },
    commentsOpen () {
      this.syncCommentsLayout()
    }
  },
  computed: {
    resolvedProductShortcodeLookup () {
      return {
        ...(this.productShortcodeLookup || {}),
        ...(this.shortcodeLookup || {})
      }
    },
    shortcodeReferences () {
      const shortcodes = this.collectProductShortcodesFromMarkdown(this.currentMarkdown || '')
      return shortcodes.map((shortcode, index) => {
        return {
          key: `${index}-${shortcode.ids.join(',')}`,
          raw: shortcode.raw,
          products: shortcode.ids.map((id) => {
            const product = this.resolvedProductShortcodeLookup[String(id)]
            return {
              key: `${index}-${id}`,
              label: this.formatShortcodeProductLabel(product, id),
              url: product?.url || '',
              missing: !product
            }
          })
        }
      })
    },
    hasShortcodeReferences () {
      return this.shortcodeReferences.length > 0
    },
    // Comments are only available over static viewer HTML - highlighting inside the
    // WYSIWYG editor would corrupt its document model.
    isViewerMode () {
      return this.readonly || this.reviewMode
    },
    canComment () {
      return this.showComments && Boolean(this.articleId) && Boolean(this.contentplanId)
    },
    decoratedThreads () {
      return this.threads.map(thread => ({
        ...thread,
        orphaned: !this.resolvedThreadIds.includes(String(thread.id))
      }))
    },
    openCommentCount () {
      return this.threads.filter(thread => thread.status === 'open').length
    }
  },
  methods: {
    ...mapActions([
      'pageGetProductsByWordpressIds',
      'articleCommentsGet'
    ]),
    async toggleVersionHistory () {
      if (!this.versionHistoryOpen && this.shortcodeReferencesOpen) this.shortcodeReferencesOpen = false
      if (!this.versionHistoryOpen && this.commentsOpen) await this.toggleComments()

      if (!this.versionHistoryOpen) {
        // Opening version history - save current content
        const currentContent = this.currentMarkdownValue()
        this.originalContentBeforePreview = currentContent
      } else if (this.isPreviewingVersion) {
        try {
          // Closing while previewing - restore original content
          this.isInternalUpdate = true
          await this.recreateEditor(this.originalContentBeforePreview || '')
          this.isPreviewingVersion = false
        } catch (error) {
          console.error('Failed to restore editor content while closing history:', error)
        } finally {
          this.isInternalUpdate = false
          this.isLoadingVersion = false
        }
      } else {
        this.isLoadingVersion = false
      }
      this.versionHistoryOpen = !this.versionHistoryOpen
    },
    toggleShortcodeReferences () {
      if (!this.hasShortcodeReferences) return
      if (!this.shortcodeReferencesOpen && this.versionHistoryOpen) this.versionHistoryOpen = false
      this.shortcodeReferencesOpen = !this.shortcodeReferencesOpen
    },
    // Opening comments on an editable editor drops it into read-only review mode,
    // and closing them puts the editor back exactly as it was.
    async toggleComments () {
      if (!this.canComment) return

      if (this.commentsOpen) {
        this.commentsOpen = false
        this.clearPendingAnchor()
        this.renderHighlights()
        if (this.reviewMode) {
          this.reviewMode = false
          await this.recreateEditor(this.currentMarkdownValue())
        }
        return
      }

      if (this.versionHistoryOpen) this.versionHistoryOpen = false
      if (this.shortcodeReferencesOpen) this.shortcodeReferencesOpen = false

      if (!this.readonly) {
        this.reviewMode = true
        await this.recreateEditor(this.currentMarkdownValue())
      }

      this.commentsOpen = true
      await this.loadComments()
    },
    // A Viewer exposes no getMarkdown(), so fall back to the value we already track.
    currentMarkdownValue () {
      if (this.editor && typeof this.editor.getMarkdown === 'function') return this.editor.getMarkdown()
      return this.currentMarkdown || this.value || ''
    },
    commentContainer () {
      const root = this.$refs.editorRoot
      if (!root) return null
      // Toast UI renders a hidden markdown-preview pane with this same class, and it
      // wins querySelector. Anchor into the surface the reader actually sees.
      const candidates = Array.from(root.querySelectorAll('.toastui-editor-contents'))
        .filter(el => !el.closest('.toastui-editor-md-preview'))
      return candidates.find(el => el.offsetWidth || el.offsetHeight) || candidates[0] || null
    },
    async loadComments () {
      if (!this.canComment) return
      this.isLoadingComments = true
      try {
        this.threads = await this.articleCommentsGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey
        })
      } catch (error) {
        console.error('Failed to load comments:', error)
      }
      this.isLoadingComments = false
      await this.$nextTick()
      this.renderHighlights()
    },
    // Re-anchors every thread against the freshly rendered article and repaints the
    // highlights. Safe to call repeatedly - it always starts from a clean container.
    renderHighlights () {
      const container = this.commentContainer()
      if (!container) return

      clearMarks(container, COMMENT_MARK_CLASS)
      if (!this.commentsOpen) return this.setResolvedThreadIds([])

      const resolved = []
      this.threads.forEach((thread) => {
        const range = resolveAnchor(container, thread)
        if (!range) return
        resolved.push(String(thread.id))
        if (thread.status !== 'open') return
        wrapRange(range, COMMENT_MARK_CLASS, { threadId: String(thread.id) })
      })

      this.setResolvedThreadIds(resolved)
      this.paintActiveThread()
    },
    setResolvedThreadIds (ids) {
      this.resolvedThreadIds = ids
    },
    paintActiveThread () {
      const container = this.commentContainer()
      if (!container) return
      container.querySelectorAll(`mark.${COMMENT_MARK_CLASS}`).forEach((mark) => {
        mark.classList.toggle(`${COMMENT_MARK_CLASS}--active`, String(this.activeThreadId) === mark.dataset.threadId)
      })
    },
    focusThread (threadId) {
      this.activeThreadId = threadId
      this.paintActiveThread()
      const mark = this.commentContainer()?.querySelector(`mark.${COMMENT_MARK_CLASS}[data-thread-id="${threadId}"]`)
      if (mark?.scrollIntoView) mark.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    // Shows the "Add comment" bubble whenever there is a live selection in the article.
    handleSelection () {
      if (!this.commentsOpen) return
      const container = this.commentContainer()
      const root = this.$refs.editorRoot
      if (!container || !root) return

      const selection = window.getSelection()
      if (!selection || selection.isCollapsed || !selection.rangeCount) return (this.commentPopover = null)

      const range = selection.getRangeAt(0)
      if (!container.contains(range.commonAncestorContainer)) return (this.commentPopover = null)

      const rect = range.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      if (!rect.width && !rect.height) return (this.commentPopover = null)

      this.commentPopover = {
        top: rect.top - rootRect.top - 40,
        left: Math.max(0, rect.left - rootRect.left)
      }
    },
    startComment () {
      const container = this.commentContainer()
      const selection = window.getSelection()
      if (!container || !selection || !selection.rangeCount) return

      // Existing marks split text nodes but never change the text itself, so the anchor
      // is the same with or without them. Describe first - clearing marks normalizes the
      // DOM and would invalidate this live selection.
      const anchor = describeRange(container, selection.getRangeAt(0))
      selection.removeAllRanges()
      this.commentPopover = null

      if (anchor?.quote?.trim()) this.pendingAnchor = anchor
      this.renderHighlights()
    },
    // The .prevent modifier does the work here - without it, pressing the bubble
    // moves focus and collapses the selection before the click lands.
    keepSelection () {},
    // Reserves room for the fixed sidebar so it never covers the article.
    syncCommentsLayout () {
      document.documentElement.classList.toggle('cg-comments-open', Boolean(this.commentsOpen && this.canComment))
    },
    clearPendingAnchor () {
      this.pendingAnchor = null
      this.commentPopover = null
    },
    async handlePreview (content, isLoading = false) {
      if (isLoading) {
        this.isLoadingVersion = true
        return
      }

      this.isLoadingVersion = false

      if (content === null) {
        // Clear preview - restore original content
        if (this.isPreviewingVersion) {
          try {
            this.isInternalUpdate = true
            await this.recreateEditor(this.originalContentBeforePreview || '')
          } catch (error) {
            console.error('Failed to restore original content:', error)
          } finally {
            this.isPreviewingVersion = false
            this.isInternalUpdate = false
          }
        }
      } else {
        // Show preview
        if (!this.isPreviewingVersion) {
          const currentContent = this.currentMarkdownValue()
          this.originalContentBeforePreview = currentContent
        }
        const previewContent = content || ''
        this.isPreviewingVersion = true
        try {
          this.isInternalUpdate = true
          await this.recreateEditor(previewContent)
          this.lastEmittedValue = previewContent
        } catch (error) {
          console.error('Failed to load preview content:', error)
        } finally {
          this.isInternalUpdate = false
        }
      }
    },
    handleRestore (content) {
      // Actual restore - emit the change
      this.isPreviewingVersion = false
      this.$emit('input', content)
      this.versionHistoryOpen = false
    },
    toggleEditMode (event) {
      if (this.readonly || this.lockMode || !this.editor || this.isViewerInstance) return

      // Check if it's cmd+s (Mac) or ctrl+s (Windows)
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault() // Prevent default save behavior
        event.stopPropagation() // Stop event from reaching the editor
        const newMode = this.currentMode === 'markdown' ? 'wysiwyg' : 'markdown'
        this.currentMode = newMode
        this.editor.changeMode(newMode)
        setTimeout(() => this.scheduleShortcodeLookupFetch(), 0)
      }
    },
    handleChange () {
      if (this.isInternalUpdate) return

      // Don't emit changes when previewing a version
      if (this.isPreviewingVersion) return

      const newValue = this.editor.getMarkdown()
      this.currentMarkdown = newValue

      // Only emit if the value actually changed to prevent unnecessary saves
      if (newValue !== this.lastEmittedValue) {
        this.lastEmittedValue = newValue
        this.$emit('input', newValue)
      }

      this.scheduleShortcodeLookupFetch(newValue)
    },
    setMarkdown (value) {
      if (!this.editor) return
      this.currentMarkdown = value || ''

      // Set the content
      this.editor.setMarkdown(value || '', false)

      // A Viewer has no modes to toggle and no internal editors to poke - it has
      // already re-rendered, so just repaint the highlights over the new content.
      if (this.isViewerInstance) {
        this.scheduleShortcodeLookupFetch(value || '')
        this.$nextTick(() => this.renderHighlights())
        return
      }

      // Try direct manipulation of the internal editor
      try {
        const mdEditor = this.editor.mdEditor
        const wwEditor = this.editor.wwEditor

        if (this.currentMode === 'markdown' && mdEditor) {
          mdEditor.setValue(value || '')
        } else if (this.currentMode === 'wysiwyg' && wwEditor) {
          // For WYSIWYG, we need to convert markdown to HTML first
          this.editor.setMarkdown(value || '', false)
        }
      } catch (e) {
        console.error('Error accessing internal editor:', e)
      }

      if (this.lockMode) {
        if (this.currentMode !== this.defaultMode) {
          this.editor.changeMode(this.defaultMode)
          this.currentMode = this.defaultMode
        }
        this.scheduleShortcodeLookupFetch(value || '')
        this.renderHighlights()
        return
      }

      // Force a refresh by toggling modes (works for both markdown and wysiwyg)
      const targetMode = this.currentMode
      const tempMode = targetMode === 'wysiwyg' ? 'markdown' : 'wysiwyg'

      // Toggle to the opposite mode and back to force a re-render
      setTimeout(() => {
        this.editor.changeMode(tempMode)
        setTimeout(() => {
          this.editor.changeMode(targetMode)
          this.currentMode = targetMode // Ensure our state is correct
          this.scheduleShortcodeLookupFetch(value || '')
          this.renderHighlights()
        }, 50)
      }, 50)
    },
    getMarkdownForShortcodeLookup (markdown) {
      if (typeof markdown === 'string') return markdown
      if (this.editor) return this.currentMarkdownValue() || ''
      return this.value || ''
    },
    truncateShortcodeLabel (value, maxLength = 100) {
      if (!value) return ''
      if (value.length <= maxLength) return value
      return `${value.slice(0, maxLength - 3)}...`
    },
    formatShortcodeProductLabel (product, id) {
      const content = product?.url
        ? this.truncateShortcodeLabel(product.url, 100)
        : product?.title || 'Unknown product'
      return `${id} | ${content}`
    },
    collectProductShortcodeIdsFromMarkdown (markdown = '') {
      if (!markdown) return []

      const shortcodePattern = /\[products?\s+ids\s*=\s*"([\d,\s]+)"[^\]]*\]/gi
      const ids = []
      let match

      while ((match = shortcodePattern.exec(markdown)) !== null) {
        const shortcodeIds = match[1].split(',').map(id => id.trim()).filter(Boolean)
        ids.push(...shortcodeIds)
      }

      return [...new Set(ids)]
    },
    collectProductShortcodesFromMarkdown (markdown = '') {
      if (!markdown) return []

      const shortcodePattern = /(\[products?\s+ids\s*=\s*"([\d,\s]+)"[^\]]*\])/gi
      const shortcodes = []
      let match

      while ((match = shortcodePattern.exec(markdown)) !== null) {
        const ids = [...new Set(match[2].split(',').map(id => id.trim()).filter(Boolean))]
        if (!ids.length) continue
        shortcodes.push({
          raw: match[1],
          ids
        })
      }

      return shortcodes
    },
    scheduleShortcodeLookupFetch (markdown) {
      if (this.shortcodeLookupTimeout) {
        clearTimeout(this.shortcodeLookupTimeout)
        this.shortcodeLookupTimeout = null
      }

      this.shortcodeLookupTimeout = setTimeout(() => {
        this.shortcodeLookupTimeout = null
        this.fetchShortcodeLookup(markdown)
      }, 200)
    },
    async fetchShortcodeLookup (markdown) {
      if (!this.contentplanId) {
        this.shortcodeLookup = {}
        this.shortcodeLookupLoadedKey = ''
        return
      }

      const currentMarkdown = this.getMarkdownForShortcodeLookup(markdown)
      const shortcodeIds = this.collectProductShortcodeIdsFromMarkdown(currentMarkdown)

      if (!shortcodeIds.length) {
        this.shortcodeLookup = {}
        this.shortcodeLookupLoadedKey = ''
        return
      }

      const requestKey = `${this.contentplanId}|${this.articleId || ''}|${this.sharingKey || ''}|${shortcodeIds.join(',')}`
      if (requestKey === this.shortcodeLookupLoadedKey || requestKey === this.shortcodeLookupRequestKey) return

      this.shortcodeLookupRequestKey = requestKey

      try {
        const response = await this.pageGetProductsByWordpressIds({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          ids: shortcodeIds
        })

        if (this.shortcodeLookupRequestKey !== requestKey) return

        this.shortcodeLookup = (response?.items || []).reduce((lookup, item) => {
          if (!item?.wordpressId) return lookup
          lookup[String(item.wordpressId)] = {
            title: item.title || item.url || `#${item.wordpressId}`,
            url: item.url || ''
          }
          return lookup
        }, {})
        this.shortcodeLookupLoadedKey = requestKey
      } catch (error) {
        console.error('Failed to load shortcode product lookup:', error)
      } finally {
        if (this.shortcodeLookupRequestKey === requestKey) {
          this.shortcodeLookupRequestKey = ''
        }
      }
    },
    handleLinkClick (event) {
      // Clicks on a comment highlight belong to the comment handler, not to links
      if (event.target?.closest?.(`mark.${COMMENT_MARK_CLASS}`)) return
      // Find the clicked link element (could be the anchor itself or a child)
      let linkElement = event.target
      while (linkElement && linkElement.tagName !== 'A') {
        linkElement = linkElement.parentElement
      }

      if (linkElement && linkElement.tagName === 'A' && linkElement.href) {
        event.preventDefault()
        event.stopPropagation()
        window.open(linkElement.href, '_blank', 'noopener,noreferrer')
      }
    },
    destroyEditorInstance () {
      if (this.shortcodeLookupTimeout) {
        clearTimeout(this.shortcodeLookupTimeout)
        this.shortcodeLookupTimeout = null
      }

      if (this.editor) {
        this.editor.destroy()
        this.editor = null
      }

      if (this.undoHandler) {
        window.removeEventListener('keydown', this.undoHandler, true)
        this.undoHandler = null
      }

      if (this.linkClickHandler && this.$refs.editorRoot) {
        this.$refs.editorRoot.removeEventListener('click', this.linkClickHandler)
        this.linkClickHandler = null
      }

      if (this.selectionHandler && this.$refs.editorRoot) {
        this.$refs.editorRoot.removeEventListener('mouseup', this.selectionHandler)
        this.selectionHandler = null
      }

      this.commentPopover = null

      if (this.$refs.editorRoot) {
        this.$refs.editorRoot.innerHTML = ''
      }
    },
    async recreateEditor (content) {
      const nextValue = content || ''
      // Wait for any pending DOM updates (e.g., preview banners) before we remount the editor
      await this.$nextTick()
      this.destroyEditorInstance()
      await this.$nextTick()

      await new Promise((resolve) => {
        const mode = this.lockMode ? this.defaultMode : (this.currentMode || this.defaultMode)
        this.initEditor(nextValue, mode, resolve)
      })
    },
    initEditor (initialValue = this.value || '', mode = this.currentMode || this.defaultMode, onReady) {
      const editorRoot = this.$refs.editorRoot
      if (!editorRoot) {
        if (typeof onReady === 'function') {
          onReady()
        }
        return
      }

      editorRoot.innerHTML = ''
      const safeValue = initialValue || ''
      const initialMode = this.lockMode ? this.defaultMode : (mode || this.defaultMode)

      const options = {
        el: editorRoot,
        initialValue: safeValue,
        minHeight: this.minHeight,
        height: this.maxHeight,
        placeholder: this.placeholder,
        initialEditType: initialMode,
        previewStyle: 'tab',
        hideModeSwitch: true,
        toolbarItems: [],
        useCommandShortcut: true,
        viewer: this.isViewerMode
      }

      // `new Editor()` ignores the viewer flag - only Editor.factory() returns a
      // Viewer, which is what actually makes the article read-only.
      this.editor = this.isViewerMode ? Editor.factory(options) : new Editor(options)
      this.isViewerInstance = this.isViewerMode

      this.currentMode = initialMode
      this.currentMarkdown = safeValue

      // Initialize last emitted value and initial content
      this.lastEmittedValue = safeValue
      this.initialContent = safeValue

      // Add event listener with debouncing for better performance
      let changeTimeout
      if (!this.isViewerInstance) {
        this.editor.on('change', () => {
          clearTimeout(changeTimeout)
          changeTimeout = setTimeout(() => {
            this.handleChange()
          }, 100) // Small debounce to prevent rapid firing
        })
      }

      // Intercept undo keyboard shortcut to prevent clearing content on first undo
      const undoHandler = (event) => {
        // Check for undo command (Ctrl/Cmd + Z)
        if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
          // Check if the event is happening within our editor
          if (editorRoot.contains(event.target)) {
            const currentContent = this.editor.getMarkdown()
            // If current content matches initial content, check if undo will clear it
            if (currentContent === this.initialContent) {
              // Let the undo happen, then check if it cleared the content
              setTimeout(() => {
                const afterUndo = this.editor.getMarkdown()
                if (!afterUndo || afterUndo === '') {
                  // Undo cleared the content, restore it
                  this.editor.setMarkdown(this.initialContent, false)
                }
              }, 0)
            }
          }
        }
      }

      // Add to window in capture phase to intercept before the editor
      if (!this.isViewerInstance) {
        window.addEventListener('keydown', undoHandler, true)
        // Store the handler so we can remove it later
        this.undoHandler = undoHandler
      }

      // Add click event listener for links to open in new tab
      this.linkClickHandler = (event) => {
        const mark = event.target?.closest?.(`mark.${COMMENT_MARK_CLASS}`)
        if (mark) return this.focusThread(mark.dataset.threadId)
        this.handleLinkClick(event)
      }
      editorRoot.addEventListener('click', this.linkClickHandler)

      if (this.canComment) {
        this.selectionHandler = () => this.handleSelection()
        editorRoot.addEventListener('mouseup', this.selectionHandler)
      }

      if (this.commentsOpen) this.$nextTick(() => this.renderHighlights())

      this.scheduleShortcodeLookupFetch(safeValue)

      if (typeof onReady === 'function') {
        onReady()
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initEditor(this.value || '', this.currentMode)
      // Add keyboard event listener
      document.addEventListener('keydown', this.toggleEditMode, true)
      // A read-only editor is always in review mode, so open comments straight away
      if (this.canComment && this.readonly) {
        this.commentsOpen = true
        this.loadComments()
      }
    })
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.toggleEditMode, true)
    if (this.commentsOpen) {
      this.commentsOpen = false
      this.syncCommentsLayout()
    }
    this.destroyEditorInstance()
  }
}
